import { useEffect, useState, useRef, useCallback } from "react";
import { api, errorMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, ArrowRight } from "lucide-react";

const ETIQUETA_ESTADO = { sin_cambios: "Sin cambios", dudoso: "Dudoso", error: "Error" };

// Modal de "Actualizar desde Honda": muestra el progreso del lote (polling cada 5s) y,
// al terminar, las propuestas de placa/chasis para aceptar o descartar antes de aplicarlas.
export function HondaActualizacionDialog({ loteId, onClose, onAplicado }) {
  const [lote, setLote] = useState(null);
  const [error, setError] = useState("");
  const [aceptados, setAceptados] = useState(new Set());
  const [aplicando, setAplicando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const intervaloRef = useRef(null);

  const cargar = useCallback(async () => {
    if (!loteId) return;
    try {
      const { data } = await api.get(`/honda-actualizaciones/lotes/${loteId}`);
      setLote(data);
      setError("");
      if (data.terminado) {
        setAceptados((prev) => (prev.size ? prev : new Set(data.items.filter((i) => i.estado === "propuesta").map((i) => i.id))));
      }
    } catch (e) {
      setError(errorMsg(e));
    }
  }, [loteId]);

  useEffect(() => {
    if (!loteId) { setLote(null); setError(""); setAceptados(new Set()); setResultado(null); return; }
    setLote(null); setError(""); setAceptados(new Set()); setResultado(null);
    cargar();
  }, [loteId, cargar]);

  useEffect(() => {
    if (!loteId || !lote || lote.terminado) return;
    intervaloRef.current = setInterval(cargar, 5000);
    return () => clearInterval(intervaloRef.current);
  }, [loteId, lote, cargar]);

  const toggleAceptado = (id) => {
    setAceptados((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const aplicar = async () => {
    setAplicando(true);
    try {
      const { data } = await api.post(`/honda-actualizaciones/lotes/${loteId}/aplicar`,
        { aceptar_ids: Array.from(aceptados) });
      setResultado(data);
      onAplicado?.();
      await cargar();
    } catch (e) { toast.error(errorMsg(e)); }
    finally { setAplicando(false); }
  };

  const elegirOpcion = async (itemId, indice) => {
    try {
      await api.post(`/honda-actualizaciones/${itemId}/elegir`, { opcion: indice });
      setAceptados((prev) => new Set(prev).add(itemId));
      setResultado(null);
      await cargar();
    } catch (e) { toast.error(errorMsg(e)); }
  };

  const items = lote?.items || [];
  const propuestas = items.filter((i) => i.estado === "propuesta");
  const otros = items.filter((i) => i.estado !== "propuesta");
  const procesados = lote ? items.length - (lote.resumen.pendiente || 0) - (lote.resumen.en_proceso || 0) : 0;

  return (
    <Dialog open={!!loteId} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="honda-actualizacion-dialog" className="max-w-2xl overflow-hidden">
        <DialogHeader>
          <DialogTitle>Actualizar desde Honda</DialogTitle>
          <DialogDescription>
            El robot consulta la placa y el chasis en Honda y propone correcciones; usted decide cuáles aplicar.
          </DialogDescription>
        </DialogHeader>

        {error ? (
          <p data-testid="honda-actualizacion-error" className="text-sm text-red-600">{error}</p>
        ) : !lote || !lote.terminado ? (
          <div data-testid="honda-actualizacion-progreso" className="flex items-center justify-center gap-2 text-sm text-slate-600 py-8">
            <Loader2 size={16} className="animate-spin" />
            Consultando Honda… {procesados} de {items.length || "…"}
          </div>
        ) : (
          <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
            {propuestas.length > 0 ? (
              <div>
                <p className="text-xs font-semibold uppercase text-slate-500 mb-1.5">Propuestas ({propuestas.length})</p>
                <div className="space-y-2">
                  {propuestas.map((i) => (
                    <label key={i.id} data-testid={`honda-actualizacion-propuesta-${i.id}`}
                      className="flex items-start gap-2.5 border border-slate-200 rounded-lg p-2.5 cursor-pointer hover:bg-slate-50">
                      <Checkbox data-testid={`honda-actualizacion-checkbox-${i.id}`} className="mt-0.5"
                        checked={aceptados.has(i.id)} onCheckedChange={() => toggleAceptado(i.id)} />
                      <div className="text-sm flex-1">
                        <p className="font-medium text-slate-800">
                          {i.vehiculo?.modelo || "—"} · {i.vehiculo?.cliente || "—"}
                        </p>
                        <div className="text-xs text-slate-600 mt-1 space-y-0.5">
                          <p className="flex items-center gap-1.5 flex-wrap">
                            Placa:
                            <span className={i.antes.placa !== i.despues.placa ? "line-through text-slate-400" : ""}>
                              {i.antes.placa || "—"}
                            </span>
                            {i.antes.placa !== i.despues.placa && (
                              <>
                                <ArrowRight size={11} />
                                <span className="font-semibold text-emerald-700">{i.despues.placa || "—"}</span>
                              </>
                            )}
                          </p>
                          <p className="flex items-center gap-1.5 flex-wrap">
                            Chasis:
                            <span className={i.antes.chasis !== i.despues.chasis ? "line-through text-slate-400" : ""}>
                              {i.antes.chasis || "—"}
                            </span>
                            {i.antes.chasis !== i.despues.chasis && (
                              <>
                                <ArrowRight size={11} />
                                <span className="font-semibold text-emerald-700">{i.despues.chasis || "—"}</span>
                              </>
                            )}
                          </p>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            ) : (
              <p data-testid="honda-actualizacion-sin-propuestas" className="text-sm text-slate-400">
                No hay propuestas de cambio en este lote.
              </p>
            )}

            {otros.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase text-slate-500 mb-1.5">Sin cambios, dudosos o con error</p>
                <div className="space-y-1.5">
                  {otros.map((i) => (
                    <div key={i.id} data-testid={`honda-actualizacion-otro-${i.id}`}
                      className="text-xs border border-slate-100 rounded-md p-2 bg-slate-50">
                      <span className="font-medium text-slate-700">{i.vehiculo?.modelo || "—"} · {i.vehiculo?.cliente || "—"}</span>
                      {" — "}
                      <span className="text-slate-500">{ETIQUETA_ESTADO[i.estado] || i.estado}</span>
                      {i.motivo && <span className="text-slate-500"> ({i.motivo})</span>}
                      {i.estado === "dudoso" && i.opciones && i.opciones.length > 0 && (
                        <div className="mt-1.5 space-y-1">
                          <div className="flex flex-wrap gap-1.5">
                            {i.opciones.map((o, idx) => (
                              <button key={idx} type="button" data-testid={`honda-actualizacion-opcion-${i.id}-${idx}`}
                                onClick={() => elegirOpcion(i.id, idx)}
                                className="text-[11px] px-2 py-1 rounded-md border border-indigo-200 bg-white text-indigo-700 hover:bg-indigo-50">
                                {o.placa || "—"} · {o.chasis || "—"} · {o.modelo || "—"}
                              </button>
                            ))}
                          </div>
                          <p className="text-[11px] text-slate-400">
                            Verifique que sea la misma moto. Solo se actualizan la placa y el chasis, no el modelo.
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {resultado && (
              <p data-testid="honda-actualizacion-resultado"
                className="text-sm text-slate-700 bg-emerald-50 border border-emerald-200 rounded-md p-2.5">
                Aplicados: {resultado.aplicadas}{resultado.con_error ? `, con error: ${resultado.con_error}` : ""}
              </p>
            )}

            {propuestas.length > 0 && !resultado && (
              <div className="flex justify-end">
                <Button data-testid="honda-actualizacion-aplicar-btn" onClick={aplicar}
                  disabled={aplicando || aceptados.size === 0}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white">
                  {aplicando ? "Aplicando…" : `Aplicar cambios (${aceptados.size})`}
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
